import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import notificationService from '../services/notificationService';
import './NotificationManagement.css';

const NotificationManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(() => ['send', 'templates', 'logs'].includes(requestedTab) ? requestedTab : 'send');
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);

  const [buyers, setBuyers] = useState([]);

  // Send notification form state
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'general',
    targetType: 'all',
    targetUsers: [],
    imageUrl: '',
    deepLink: '',
    scheduledAt: ''
  });

  // Template form state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    title: '',
    message: '',
    type: 'general',
    variables: [],
    imageUrl: ''
  });

  useEffect(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('tab', activeTab);
      return next;
    }, { replace: true });
  }, [activeTab, setSearchParams]);

  useEffect(() => {
    loadTemplates();
    loadLogs();
    loadBuyers();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await notificationService.getTemplates();
      if (response.success) {
        const loadedTemplates = response.data.templates || [];
        // Combine loaded templates with default templates, avoiding duplicates
        const templateIds = new Set(loadedTemplates.map(t => t.id));
        const defaultTemplates = DEFAULT_TEMPLATES.filter(t => !templateIds.has(t.id));
        setTemplates([...loadedTemplates, ...defaultTemplates]);
      } else {
        // If no templates from API, use defaults
        setTemplates(DEFAULT_TEMPLATES);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      // Use default templates on error
      setTemplates(DEFAULT_TEMPLATES);
    }
  };

  const loadLogs = async () => {
    try {
      const response = await notificationService.getLogs();
      if (response.success) {
        setLogs(response.data.logs);
      }
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  };



  const loadBuyers = async () => {
    try {
      const response = await notificationService.getBuyers();
      if (response.success) {
        setBuyers(response.data.users || []);
      }
    } catch (error) {
      console.error('Error loading buyers:', error);
    }
  };

  // Default notification templates
  const DEFAULT_TEMPLATES = [
    {
      id: 'order_confirmed',
      name: 'Order Confirmed',
      title: 'Order #{order_id} Confirmed',
      message: 'Your order has been confirmed. We will process it shortly.',
      type: 'order',
      variables: ['order_id', 'order_date']
    },
    {
      id: 'order_shipped',
      name: 'Order Shipped',
      title: 'Order #{order_id} Shipped',
      message: 'Your order has been shipped. Tracking ID: {tracking_id}',
      type: 'order',
      variables: ['order_id', 'tracking_id']
    },
    {
      id: 'payment_received',
      name: 'Payment Received',
      title: 'Payment Received - ₹{amount}',
      message: 'We have received your payment of ₹{amount}. Thank you!',
      type: 'payment',
      variables: ['amount', 'transaction_id']
    },
    {
      id: 'neft_pending',
      name: 'NEFT Pending',
      title: 'NEFT Transfer Pending',
      message: 'Your NEFT transfer of ₹{amount} is pending. Reference: {reference_id}',
      type: 'neft',
      variables: ['amount', 'reference_id']
    },
    {
      id: 'new_offer',
      name: 'New Offer Available',
      title: 'Special Offer: {offer_name}',
      message: 'Check out our new offer on {product_category}. Save up to {discount}%!',
      type: 'offer',
      variables: ['offer_name', 'product_category', 'discount']
    },
    {
      id: 'stock_available',
      name: 'Stock Available',
      title: '{product_name} Back in Stock',
      message: 'The product you were waiting for is now available. Limited quantity!',
      type: 'stock',
      variables: ['product_name', 'quantity']
    },
    {
      id: 'account_approved',
      name: 'Account Approved',
      title: 'Account Approved',
      message: 'Congratulations! Your account has been approved. You can now place orders.',
      type: 'account',
      variables: []
    },
    {
      id: 'reward_earned',
      name: 'Reward Points Earned',
      title: 'You Earned {points} Reward Points',
      message: 'You have earned {points} reward points from your recent purchase.',
      type: 'reward',
      variables: ['points', 'order_id']
    },
    {
      id: 'referral_bonus',
      name: 'Referral Bonus',
      title: 'Referral Bonus: ₹{bonus_amount}',
      message: 'Your friend {friend_name} joined using your referral code. You earned ₹{bonus_amount}!',
      type: 'referral',
      variables: ['friend_name', 'bonus_amount']
    },
    {
      id: 'price_drop',
      name: 'Price Drop Alert',
      title: 'Price Drop: {product_name}',
      message: 'The price of {product_name} has dropped to ₹{new_price}. Save ₹{savings}!',
      type: 'offer',
      variables: ['product_name', 'new_price', 'savings']
    }
  ];

  const handleSendNotification = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await notificationService.sendNotification(formData);

      if (response.success) {
        alert('Notification sent successfully!');
        setFormData({
          title: '',
          message: '',
          type: 'general',
          targetType: 'all',
          targetUsers: [],
          imageUrl: '',
          deepLink: '',
          scheduledAt: ''
        });
        loadLogs();
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await notificationService.createTemplate(templateForm);

      if (response.success) {
        alert('Template created successfully!');
        setTemplateForm({
          name: '',
          title: '',
          message: '',
          type: 'general',
          variables: [],
          imageUrl: ''
        });
        loadTemplates();
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = (template) => {
    setFormData({
      ...formData,
      title: template.title,
      message: template.message,
      type: template.type,
      imageUrl: template.image_url || ''
    });
    setActiveTab('send');
  };

  const renderSendTab = () => (
    <div className="notification-send-tab">
      <form onSubmit={handleSendNotification}>
        <div className="form-group">
          <label>Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            maxLength={255}
            placeholder="Enter notification title"
          />
        </div>

        <div className="form-group">
          <label>Message *</label>
          <textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            required
            rows={4}
            placeholder="Enter notification message"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Type *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              required
            >
              <option value="general">General</option>
              <option value="order">Order</option>
              <option value="payment">Payment</option>
              <option value="neft">NEFT</option>
              <option value="offer">Offer</option>
              <option value="stock">Stock</option>
              <option value="account">Account</option>
              <option value="reward">Reward</option>
              <option value="referral">Referral</option>
            </select>
          </div>

          <div className="form-group">
            <label>Target *</label>
            <select
              value={formData.targetType}
              onChange={(e) => setFormData({ ...formData, targetType: e.target.value })}
              required
            >
              <option value="all">All Buyers</option>
              <option value="approved">Approved Buyers Only</option>
              <option value="specific">Specific Buyers</option>
              <option value="inactive">Inactive Buyers (30+ days)</option>
            </select>
          </div>
        </div>

        {formData.targetType === 'specific' && (
          <div className="form-group">
            <label>Select Buyers *</label>
            <select
              multiple
              value={formData.targetUsers}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                setFormData({ ...formData, targetUsers: selected });
              }}
              size={5}
            >
              {buyers.map(buyer => (
                <option key={buyer.id} value={buyer.id}>
                  {buyer.name} ({buyer.email})
                </option>
              ))}
            </select>
            <small>Hold Ctrl/Cmd to select multiple buyers</small>
          </div>
        )}

        <div className="form-group">
          <label>Image URL (Optional)</label>
          <input
            type="url"
            value={formData.imageUrl}
            onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
            placeholder="https://example.com/image.jpg"
          />
        </div>

        <div className="form-group">
          <label>Deep Link (Optional)</label>
          <input
            type="text"
            value={formData.deepLink}
            onChange={(e) => setFormData({ ...formData, deepLink: e.target.value })}
            placeholder="app://orders/123"
          />
        </div>

        <div className="form-group">
          <label>Schedule (Optional)</label>
          <input
            type="datetime-local"
            value={formData.scheduledAt}
            onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
          />
          <small>Leave empty to send immediately</small>
        </div>

        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Sending...' : formData.scheduledAt ? 'Schedule Notification' : 'Send Now'}
        </button>
      </form>
    </div>
  );

  const renderTemplatesTab = () => (
    <div className="notification-templates-tab">
      <div className="templates-section">
        <h3>Available Templates</h3>
        <div className="templates-grid">
          {templates.map(template => (
            <div key={template.id} className="template-card">
              <div className="template-header">
                <span className={`template-type type-${template.type}`}>
                  {template.type}
                </span>
                <h4>{template.name}</h4>
              </div>
              <div className="template-content">
                <p className="template-title">{template.title}</p>
                <p className="template-message">{template.message}</p>
                {template.variables && template.variables.length > 0 && (
                  <div className="template-variables">
                    <small>Variables: {template.variables.join(', ')}</small>
                  </div>
                )}
              </div>
              <button
                className="btn-secondary"
                onClick={() => handleUseTemplate(template)}
              >
                Use Template
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="create-template-section">
        <h3>Create New Template</h3>
        <form onSubmit={handleCreateTemplate}>
          <div className="form-group">
            <label>Template Name *</label>
            <input
              type="text"
              value={templateForm.name}
              onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
              required
              placeholder="e.g., order_shipped"
            />
          </div>

          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={templateForm.title}
              onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
              required
              placeholder="Use {variable_name} for dynamic content"
            />
          </div>

          <div className="form-group">
            <label>Message *</label>
            <textarea
              value={templateForm.message}
              onChange={(e) => setTemplateForm({ ...templateForm, message: e.target.value })}
              required
              rows={3}
              placeholder="Use {variable_name} for dynamic content"
            />
          </div>

          <div className="form-group">
            <label>Type *</label>
            <select
              value={templateForm.type}
              onChange={(e) => setTemplateForm({ ...templateForm, type: e.target.value })}
              required
            >
              <option value="general">General</option>
              <option value="order">Order</option>
              <option value="payment">Payment</option>
              <option value="neft">NEFT</option>
              <option value="offer">Offer</option>
              <option value="stock">Stock</option>
              <option value="account">Account</option>
              <option value="reward">Reward</option>
              <option value="referral">Referral</option>
            </select>
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create Template'}
          </button>
        </form>
      </div>
    </div>
  );

  const renderLogsTab = () => (
    <div className="notification-logs-tab">
      <table className="logs-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Title</th>
            <th>Type</th>
            <th>Target</th>
            <th>Recipients</th>
            <th>Sent</th>
            <th>Delivered</th>
            <th>Failed</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {logs.map(log => (
            <tr key={log.id}>
              <td>{new Date(log.created_at).toLocaleString()}</td>
              <td>{log.title}</td>
              <td>
                <span className={`badge badge-${log.type}`}>{log.type}</span>
              </td>
              <td>{log.target_type}</td>
              <td>{log.total_recipients}</td>
              <td>{log.sent_count}</td>
              <td>{log.delivered_count}</td>
              <td>{log.failed_count}</td>
              <td>
                <span className={`status status-${log.status}`}>{log.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="notification-management">
      <div className="notification-header">
        <h2>Notification Management</h2>
        <p>Send push notifications to your buyers</p>
      </div>

      <div className="notification-tabs">
        <button
          className={activeTab === 'send' ? 'active' : ''}
          onClick={() => setActiveTab('send')}
        >
          Send Notification
        </button>
        <button
          className={activeTab === 'templates' ? 'active' : ''}
          onClick={() => setActiveTab('templates')}
        >
          Templates
        </button>
        <button
          className={activeTab === 'logs' ? 'active' : ''}
          onClick={() => setActiveTab('logs')}
        >
          History
        </button>
      </div>

      <div className="notification-content">
        {activeTab === 'send' && renderSendTab()}
        {activeTab === 'templates' && renderTemplatesTab()}
        {activeTab === 'logs' && renderLogsTab()}
      </div>
    </div>
  );
};

export default NotificationManagement;
